import type { ModelProps, ModelRegistry } from '../types';

function FrozenTree({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Bare trunk */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <cylinderGeometry args={[0.08, 0.15, 1.2, 6]} />
        <meshStandardMaterial
          color={world === 'real' ? '#4a4a4a' : '#3a3a4a'}
          roughness={0.9}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Bare branches */}
      {[[0.4, 0.9, 0, 0.5], [-0.3, 1.1, 0.1, -0.6], [0.2, 0.7, -0.2, 0.4]].map(([x, y, z, rot], i) => (
        <mesh key={i} position={[x * 0.5, y, z]} rotation={[0.2, 0, rot]} castShadow>
          <cylinderGeometry args={[0.02, 0.04, 0.4, 4]} />
          <meshStandardMaterial
            color={world === 'real' ? '#3a3a3a' : '#2a2a3a'}
            roughness={0.9}
            transparent={isOccluding}
            opacity={opacity}
          />
        </mesh>
      ))}
      {/* Ice/frost coating */}
      <mesh position={[0, 0.65, 0]}>
        <cylinderGeometry args={[0.1, 0.17, 1.3, 6]} />
        <meshStandardMaterial
          color={world === 'real' ? '#E0FFFF' : '#B0C4DE'}
          transparent
          opacity={0.3}
          roughness={0.1}
        />
      </mesh>
    </group>
  );
}

function IceCrystal({ world }: ModelProps) {
  return (
    <group>
      {/* Main crystal */}
      <mesh position={[0, 0.3, 0]} rotation={[0, 0.3, 0.1]} castShadow>
        <octahedronGeometry args={[0.2]} />
        <meshStandardMaterial
          color={world === 'real' ? '#E0FFFF' : '#B0E0E6'}
          emissive={world === 'spectral' ? '#87CEEB' : '#E0FFFF'}
          emissiveIntensity={world === 'spectral' ? 0.4 : 0.1}
          transparent
          opacity={0.8}
          roughness={0.1}
          metalness={0.2}
        />
      </mesh>
      {/* Smaller crystals */}
      {[[0.15, 0.15, 0.1, 0.6], [-0.1, 0.2, -0.12, 0.8], [0.08, 0.1, -0.15, 0.4]].map(([x, y, z, s], i) => (
        <mesh key={i} position={[x, y, z]} rotation={[i * 0.3, i * 0.5, 0.2]} scale={s}>
          <octahedronGeometry args={[0.12]} />
          <meshStandardMaterial
            color={world === 'real' ? '#F0FFFF' : '#ADD8E6'}
            emissive={world === 'spectral' ? '#87CEEB' : undefined}
            emissiveIntensity={0.3}
            transparent
            opacity={0.7}
            roughness={0.1}
          />
        </mesh>
      ))}
    </group>
  );
}

function SnowPile({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      <mesh position={[0, 0.1, 0]} castShadow receiveShadow>
        <sphereGeometry args={[0.3, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial
          color={world === 'real' ? '#FFFAFA' : '#E8E8F0'}
          roughness={0.6}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Smaller mounds */}
      <mesh position={[0.2, 0.06, 0.15]}>
        <sphereGeometry args={[0.15, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color={world === 'real' ? '#FFFAFA' : '#E8E8F0'} roughness={0.6} />
      </mesh>
      <mesh position={[-0.15, 0.05, -0.1]}>
        <sphereGeometry args={[0.12, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color={world === 'real' ? '#FFFAFA' : '#E8E8F0'} roughness={0.6} />
      </mesh>
    </group>
  );
}

function FrostGrass({ world }: ModelProps) {
  return (
    <group>
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const angle = (i * Math.PI * 2) / 8;
        return (
          <mesh key={i} position={[Math.cos(angle) * 0.06, 0.1, Math.sin(angle) * 0.06]} rotation={[0.1 * Math.sin(i), 0, 0.08 * Math.cos(i)]}>
            <coneGeometry args={[0.015, 0.2, 4]} />
            <meshStandardMaterial
              color={world === 'real' ? '#B0C4DE' : '#8090A0'}
              roughness={0.5}
            />
          </mesh>
        );
      })}
    </group>
  );
}

export const iceModels: ModelRegistry = {
  frozen_tree: FrozenTree,
  ice_crystal: IceCrystal,
  snow_pile: SnowPile,
  frost_grass: FrostGrass,
};
